import { Grid, useMediaQuery, useTheme, Box, Typography, CircularProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { GridWrapper, Heading, SubHeading } from './styles';
import CSPDetails from './CSPDetails';
import ShipmentSummaryPickupType from './ShipmentSummaryPickupType';
import SbTextField from '../../../components/Textfield';
import Charges from './Charges';
import CreateShipmentContext from '../../../context/CreateShipment';
import { createShipmentContextType } from '../../../types/shipment';
import { AppDispatch, RootStateType } from '../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { placeShipment, redirectToSuccessPage, resetEditShipment } from '../../../store/reducers/Shipment';
import Divider from '../../../components/Divider';
import {
  ShipperConsigneeSummaryDetails,
  EditAction,
} from '../../../components/Shipment/Summary/ShipperConsigneeSummaryDetails';
import ShipmentSummaryDetails from '../../../components/Shipment/Summary/ShipmentSummaryDetails';
import { ShipmentType } from '../../../store/reducers/Shipment/Type';
import { PayNow } from '@Components/PayNow';
import { getAddressObj, getSingleShipmentPackagePayload } from '@Services/getShipmentPayload';
import { usePayment } from '@Hook/usePayment';
import SbButton from '@Components/Button';
import { ErrorType } from './Type';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import API from '@Services/apiAxios';
import { isDutyChargesAllowedSubdomain } from '../../../utils/getSubdomain';

const getDutyChargeType = (dutyCharge: any): string => {
  if (!dutyCharge) return '';
  if (typeof dutyCharge === 'string') return dutyCharge;
  if (typeof dutyCharge === 'object' && typeof dutyCharge.type === 'string') return dutyCharge.type;
  return '';
};

function ShipmentSummary() {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setOpenPaymentModal, hasPaymentAccess } = usePayment();
  const {
    shipper,
    consignee,
    shipmentType,
    extraDetails,
    packageData,
    notes,
    boxes,
    pickupType,
    // chargeableWeight,
    setNotes,
    setShipper,
    setConsignee,
    userId,
    selectedCSP,
  } = React.useContext(CreateShipmentContext) as createShipmentContextType;

  const { shipmentSummary, editShipment, finalChargeableWeight } = useSelector(
    (state: RootStateType) => state.shipment,
  );
  const { shipmentSetting, organizationPublicData } = useSelector((state: RootStateType) => state?.orgSetup);
  const CSPChargesData = useSelector((state: RootStateType) => state?.getRates?.CSPChargesData);

  const orgCountryName = organizationPublicData?._country?.name?.trim() || '';

  const getCountryName = (field: any): string => {
    if (!field) return '';
    if (typeof field === 'string') return field.trim();
    if (typeof field === 'object') {
      if (typeof field.country === 'string') return field.country.trim();
      if (field.country && typeof field.country === 'object' && typeof field.country.name === 'string') {
        return field.country.name.trim();
      }
    }
    return '';
  };

  const shipperCountry = getCountryName(shipper);
  const consigneeCountry = getCountryName(consignee);

  const [loadingDocs, setLoadingDocs] = useState<boolean>(false);
  const [clearanceDocs, setClearanceDocs] = useState<string[]>([]);
  const [docsError, setDocsError] = useState<string | null>(null);

  // Helper to format pickup date to RFC 7231 / UTC
  const formatPickupDate = (date: any): string => {
    if (!date) return '';
    if (typeof date.toDate === 'function') {
      return date.toDate().toUTCString();
    }
    const parsed = new Date(date);
    if (!isNaN(parsed.getTime())) {
      return parsed.toUTCString();
    }
    return String(date);
  };

  const dutyChargeAmount =
    CSPChargesData?.dutyChargeAmount !== undefined && CSPChargesData?.dutyChargeAmount !== null
      ? Number(CSPChargesData.dutyChargeAmount)
      : 0;

  const dutyChargeType = getDutyChargeType(shipmentType.dutyCharge);

  const requestPayload = React.useMemo(() => {
    return {
      consignee: {
        country: consigneeCountry || '',
      },
      package: {
        unitOfMeasurement: packageData?.unitOfMeasurement || shipmentType?.unit || '',
        packaging: !!packageData?.packaging,
        insurance: !!packageData?.insurance,
        isDocument: shipmentType?.type === 'document',
        isCod: !!packageData?.isCod,
        isDropOff: pickupType?.type === 'dropOff',
        pickupDate: formatPickupDate(pickupType?.date),
        timeSlot: pickupType?.timeSlot || '',
        codAmount: Number(packageData?.codAmount) || 0,
        currency: shipmentType?.currency || '',
        box: (boxes || []).map((box, idx) => ({
          length: Number(box.length) || 0,
          width: Number(box.width) || 0,
          height: Number(box.height) || 0,
          weight: Number(box.weight) || 0,
          id: box.id || idx.toString(),
          products: (box.products || []).map((prod: any) => ({
            description: prod.description || '',
            quantity: Number(prod.quantity) || 0,
            unitValue: Number(prod.unitValue) || 0,
            HSNName: prod.HSNName || '',
            HSNCode: prod.HSNCode || '',
            productId: prod.productId ? String(prod.productId).trim() : '',
            productIdType: prod.productIdType ? String(prod.productIdType).trim() : 'SKU',
            merchantProductId: prod.merchantProductId ? String(prod.merchantProductId).trim() : '',
            standardManufacturerProductId: prod.standardManufacturerProductId
              ? String(prod.standardManufacturerProductId).trim()
              : '',
            nonStandardManufacturerProductId: prod.nonStandardManufacturerProductId
              ? String(prod.nonStandardManufacturerProductId).trim()
              : '',
            _product: prod._product || null,
            _id: prod._id || prod.id || '',
            createdAt: prod.createdAt || new Date().toISOString(),
            updatedAt: prod.updatedAt || new Date().toISOString(),
          })),
        })),
        totalBoxWeight: 'Need calc',
        invoiceNumber: shipmentType?.invoiceNumber || packageData?.invoiceNumber || '',
        ...(isDutyChargesAllowedSubdomain() &&
          dutyChargeType && {
            dutyCharge: {
              type: dutyChargeType,
              charge: isNaN(dutyChargeAmount) ? 0 : dutyChargeAmount,
            },
          }),
      },
      purposeOfShipment: extraDetails?.purposeOfShipment || 'FOR_SALE',
    };
  }, [
    consigneeCountry,
    packageData,
    shipmentType,
    pickupType,
    boxes,
    extraDetails?.purposeOfShipment,
    dutyChargeType,
    dutyChargeAmount,
  ]);

  const requestPayloadStr = React.useMemo(() => JSON.stringify(requestPayload), [requestPayload]);

  useEffect(() => {
    if (!consigneeCountry) {
      return;
    }

    let active = true;

    const fetchClearanceDocs = async () => {
      setLoadingDocs(true);
      setDocsError(null);
      try {
        const response = await API.post('/speedboxbot/clearance-documents', JSON.parse(requestPayloadStr));

        if (active) {
          const docs = response?.data?.documents || [];
          const docNames = docs.map((doc: any) => doc.documentName).filter(Boolean);
          setClearanceDocs(docNames);
        }
      } catch (err) {
        console.error('Error fetching clearance documents:', err);
        if (active) {
          setDocsError('Failed to fetch required clearance documents.');
        }
      } finally {
        if (active) {
          setLoadingDocs(false);
        }
      }
    };

    fetchClearanceDocs();

    return () => {
      active = false;
    };
  }, [requestPayloadStr, consigneeCountry]);

  const checkCountries = [shipperCountry, consigneeCountry].filter(Boolean);

  const isShipmentExport = orgCountryName
    ? checkCountries.some(c => c.toLowerCase() !== orgCountryName.toLowerCase())
    : false;

  const { pickupTimeSlots } = shipmentSetting || {};
  const [errors, setErrors] = useState<ErrorType>();

  const shipmentNumber = shipmentSummary?.shipmentNumber || '';
  const sellingPricing = selectedCSP?.totalPricing;

  const { warehouse, selectedWearhouseAddress } = pickupType;
  const { shipmentMode, referenceNumber } = shipmentType;
  const { insurance, packaging, unitOfMeasurement } = packageData;

  useEffect(() => {
    return () => {
      dispatch(resetEditShipment());
    };
  }, []);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setNotes(value);
  }

  function handleDetailsChange(event: React.ChangeEvent<HTMLInputElement>, label: string) {
    const { value } = event.target;
    if (label === 'Shipper') {
      setShipper({ ...shipper, name: value });
    } else if (label === 'Consignee') {
      setConsignee({ ...consignee, name: value });
    }
  }

  function handlePlaceOrder() {
    const errorData: ErrorType = {};
    if (!shipper.name) {
      errorData['shipperName'] = 'Please add name.';
      setErrors(errorData);
    }
    if (!consignee.name) {
      errorData['consigneeName'] = 'Please add name.';
      setErrors(errorData);
    }
    if (pickupType.type === 'dropOff' && !pickupType.warehouse) {
      errorData['warehouse'] = 'Please choose the drop-off warehouse.';
      setErrors(errorData);
      return;
    } else if (pickupType.type === 'pickUp') {
      if (!pickupType.date) {
        errorData['date'] = 'Please choose a pickup date ';
        setErrors(errorData);
        return;
      }
      if (pickupTimeSlots?.slots?.length && !pickupType.timeSlot) {
        errorData['timeSlot'] = 'Please choose a  time slot.';
        setErrors(errorData);
        return;
      }
    }

    if ((!errorData || (errorData && !Object.keys(errorData).length)) && editShipment) {
      setErrors(undefined);
      let packageObj = getSingleShipmentPackagePayload({ boxes, packageData, shipmentType, pickupType });
      const hurricaneProducts = CSPChargesData?.hurricaneData?.products;
      if (isShipmentExport && Array.isArray(hurricaneProducts) && hurricaneProducts.length > 0) {
        packageObj = JSON.parse(JSON.stringify(packageObj));
        if (Array.isArray(packageObj.box)) {
          packageObj.box.forEach((box: any) => {
            if (Array.isArray(box.products)) {
              box.products.forEach((prod: any) => {
                const match = hurricaneProducts.find(
                  (hp: any) => hp.description?.toLowerCase().trim() === prod.description?.toLowerCase().trim(),
                );
                if (match && match.suitableImportCode) {
                  prod.HSNCode = match.suitableImportCode;
                }
              });
            }
          });
        }
      }

      if (isDutyChargesAllowedSubdomain() && getDutyChargeType(shipmentType.dutyCharge)) {
        const dutyChargeAmount =
          CSPChargesData?.dutyChargeAmount !== undefined && CSPChargesData?.dutyChargeAmount !== null
            ? Number(CSPChargesData.dutyChargeAmount)
            : 0;

        const formattedDutyCharge = {
          type: getDutyChargeType(shipmentType.dutyCharge),
          charge: isNaN(dutyChargeAmount) ? 0 : dutyChargeAmount,
        };

        packageObj.dutyCharge = formattedDutyCharge;
      }

      const postObj: ShipmentType = {
        package: packageObj,
        shipper: getAddressObj(shipper),
        consignee: getAddressObj(consignee),
        shipmentNotes: notes,
        shipmentMode: shipmentType.shipmentMode,
        userReferenceNumber: shipmentType.referenceNumber || '',
        selectedCourierId: selectedCSP?._courierId,
        selectedSystemId: selectedCSP?._systemId,
        ...(isDutyChargesAllowedSubdomain() &&
          getDutyChargeType(shipmentType.dutyCharge) && {
            dutyCharge: {
              type: getDutyChargeType(shipmentType.dutyCharge),
              charge: isNaN(
                CSPChargesData?.dutyChargeAmount !== undefined && CSPChargesData?.dutyChargeAmount !== null
                  ? Number(CSPChargesData.dutyChargeAmount)
                  : 0,
              )
                ? 0
                : Number(CSPChargesData?.dutyChargeAmount || 0),
            },
          }),
        extraDetails: {
          shipper: {
            type: shipper.type,
            IdType: shipper.IdType,
            IdNumber: shipper.IdNumber,
          },
          consignee: {
            type: consignee.type,
            IdType: consignee.IdType,
            IdNumber: consignee.IdNumber,
          },
          other: extraDetails,
        },
      };
      if (pickupType.type === 'dropOff') {
        postObj.warehouseAddress = selectedWearhouseAddress;
        postObj.warehouseId = warehouse;
      }
      dispatch(placeShipment({ postObj: { ...postObj }, userId, showPaymentModal: hasPaymentAccess }));
    } else {
      setOpenPaymentModal(true);
    }
  }

  function handlePaymentSuccess() {
    dispatch(redirectToSuccessPage(true));
  }

  return (
    <Grid>
      <Heading className="mb-3">Summary</Heading>
      <Grid container sx={{ p: { xs: 0, lg: 3 } }} spacing={3}>
        <Grid item lg={12} xs={12}>
          <GridWrapper className="b-0" hasBackground>
            <Grid container justifyContent={'space-between'}>
              <SubHeading>Service</SubHeading>
              {editShipment && isMobile && (
                <Grid item>
                  <EditAction activeStep={3} />
                </Grid>
              )}
            </Grid>
            <Grid container justifyContent={'space-between'}>
              <Grid item lg={11}>
                <CSPDetails />
              </Grid>
              {editShipment && !isMobile && (
                <Grid item>
                  <EditAction activeStep={3} />
                </Grid>
              )}
            </Grid>
          </GridWrapper>
        </Grid>
        <Grid container item lg={8} xs={12} spacing={3}>
          <Grid item lg={12}>
            <GridWrapper container>
              <Grid container item lg={6} className={isMobile ? '' : 'border-right pr-3'}>
                <ShipperConsigneeSummaryDetails
                  label="Shipper"
                  {...shipper}
                  activeStep={0}
                  handleChange={handleDetailsChange}
                  editShipment={editShipment}
                  error={errors?.shipperName || ''}
                />
              </Grid>
              {isMobile && <Divider className="my-2" />}
              <Grid container item lg={6} sx={{ pl: { lg: 3, xs: 0 } }}>
                <ShipperConsigneeSummaryDetails
                  label="Consignee"
                  {...consignee}
                  activeStep={1}
                  handleChange={handleDetailsChange}
                  editShipment={editShipment}
                  error={errors?.consigneeName || ''}
                />
              </Grid>
            </GridWrapper>
          </Grid>
          <Grid item lg={12}>
            <GridWrapper container>
              <ShipmentSummaryPickupType errorData={errors} />
            </GridWrapper>
          </Grid>
          <Grid item lg={12}>
            <GridWrapper container>
              <ShipmentSummaryDetails
                boxes={boxes.length}
                // chargeableWeight={chargeableWeight}
                chargeableWeight={finalChargeableWeight}
                shipmentType={shipmentType.type}
                shipmentMode={shipmentMode}
                insurance={insurance}
                packaging={packaging}
                unitOfMeasurement={unitOfMeasurement}
                activeStep={2}
                editShipment={editShipment}
                referenceNumber={referenceNumber}
                purposeOfShipment={extraDetails?.purposeOfShipment}
                ior={extraDetails?.ior}
              />
              {isMobile && <Divider className="my-2" />}
              <Grid container item xs={12} lg={6} sx={{ pl: { lg: 3, xs: 0 } }}>
                <Grid item lg={12} xs={12}>
                  <SubHeading>Add a Note</SubHeading>
                  <SbTextField
                    placeholder="Enter a description..."
                    // label="Description"
                    onChange={handleChange}
                    name="description"
                    value={notes}
                    required
                    // error={errors.description}
                    rows={8}
                    multiline
                    className="w-full"
                  />
                </Grid>
              </Grid>
            </GridWrapper>
          </Grid>
          <Grid item lg={12} xs={12}>
            <GridWrapper sx={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
              <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
                {/* Header Row */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    width: '100%',
                    mb: 2.5,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          borderRadius: '10px',
                          bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                          color: 'primary.main',
                          flexShrink: 0,
                        }}
                      >
                        <DescriptionIcon sx={{ fontSize: '20px' }} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '18px',
                          fontWeight: 600,
                          color: theme => theme.palette.text.purple || '#1F1E61',
                          letterSpacing: '-0.01em',
                          lineHeight: 1.2,
                        }}
                      >
                        Required Clearance Documents
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: 'text.light',
                        pl: { xs: 0, sm: '48px' },
                        lineHeight: 1.4,
                      }}
                    >
                      The following documents will be required for customs clearance and processing.
                    </Typography>
                  </Box>
                  {!loadingDocs && !docsError && clearanceDocs.length > 0 && (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '16px',
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                        color: 'primary.main',
                        fontSize: '12px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                        flexShrink: 0,
                        alignSelf: { xs: 'flex-start', sm: 'center' },
                      }}
                    >
                      {clearanceDocs.length} {clearanceDocs.length === 1 ? 'Document' : 'Documents'}
                    </Box>
                  )}
                </Box>

                {/* Content Area */}
                {loadingDocs ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2.5 }}>
                    <CircularProgress size={20} thickness={4} />
                    <Typography sx={{ color: 'text.light', fontSize: '13.5px' }}>
                      Fetching required clearance documents...
                    </Typography>
                  </Box>
                ) : docsError ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1.5,
                      px: 2,
                      borderRadius: '8px',
                      bgcolor: theme => alpha(theme.palette.error.main, 0.05),
                      border: theme => `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <InfoOutlinedIcon color="error" sx={{ fontSize: '20px', flexShrink: 0 }} />
                    <Typography sx={{ color: 'error.main', fontSize: '13px', fontWeight: 500 }}>
                      Failed to load required clearance documents. You can still proceed with placing the shipment.
                    </Typography>
                  </Box>
                ) : clearanceDocs.length === 0 ? (
                  <Box
                    sx={{
                      py: 2.5,
                      px: 2,
                      borderRadius: '8px',
                      bgcolor: 'background.paper',
                      border: '1px dashed',
                      borderColor: 'divider',
                      textAlign: 'center',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Typography sx={{ color: 'text.light', fontSize: '13.5px' }}>
                      No specific clearance documents are required for this shipment.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      columnGap: 4,
                      rowGap: 0,
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    {clearanceDocs.map((docName, idx) => (
                      <Box
                        key={`${docName}-${idx}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 1.25,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          minWidth: 0,
                          boxSizing: 'border-box',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                            color: 'primary.main',
                            flexShrink: 0,
                            mr: 1.5,
                          }}
                        >
                          <DescriptionIcon sx={{ fontSize: '14px' }} />
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: '13.5px',
                            color: theme => theme.palette.text.dark || '#344054',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          }}
                          title={docName}
                        >
                          {docName}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </GridWrapper>
          </Grid>
        </Grid>
        <Grid container item lg={4} xs={12}>
          <Grid item xs={12} sm={12} lg={12}>
            <Charges />
          </Grid>
        </Grid>
      </Grid>
      <Grid container justifyContent={'center'}>
        {!hasPaymentAccess ? (
          <SbButton
            onClick={handlePlaceOrder}
            variant="contained"
            style={{ height: '44px' }}
            sx={{ my: { lg: 0, xs: 3 } }}
          >
            Place Shipment
          </SbButton>
        ) : (
          <Grid item xs={2}>
            <PayNow
              handlePayNow={handlePlaceOrder}
              paymentDetails={{
                shipmentNumber,
                payableAmount: Number(sellingPricing),
                handlePaymentSuccess,
              }}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

export default ShipmentSummary;
